"use client";

import Image from "next/image";
import Link from "next/link";
import { fontWeights } from "@/utils/fonts";

interface ProjectDetailsProps {
  headline?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  heroImage?: string;
  industry?: string;
  solution?: string;
  tags?: string[];
}

export function ProjectDetails({
  headline,
  heroTitle,
  heroSubtitle,
  heroImage,
  industry,
  solution,
  tags
}: ProjectDetailsProps) {
  return (
    <>
      {/* Hero Section */}
      <section className="px-4 sm:px-6 pt-6">
        <div className="max-w-[1313px] mx-auto text-left">
          <nav className="flex items-center flex-wrap gap-2 mb-4 text-[12px] sm:text-[13px] md:text-[15px] font-medium text-[#1e293b]" style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.medium }}>
            <Link href="/" className="hover:text-[#005bb7] transition-colors">Home</Link>
            <span className="text-[#64748b] font-bold">&gt;</span>
            <Link href="/portfolio" className="hover:text-[#005bb7] transition-colors">Portfolio</Link>
            <span className="text-[#64748b] font-bold">&gt;</span>
            <span className="text-[#1e293b]">{headline}</span>
          </nav>
          <hr className="border-t border-[#e2e8f0] mb-8 sm:mb-10 lg:mb-14 xl:mb-[70px] w-full" />
          <h2
            className="text-[15px] sm:text-[16px] lg:text-[18px] xl:text-[20px] text-[#09155E] mb-4 lg:mb-[22px]"
            style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.bold }}
          >
            {heroSubtitle}
          </h2>

          <h1
            className="text-[22px] md:text-[24px] lg:text-[28px] xl:text-[32px] text-[#09155E] mb-4 sm:mb-8 md:mb-12 lg:mb-16 xl:mb-20 leading-tight"
            style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.extrabold }}
          >
            {heroTitle}
          </h1>
        </div>
      </section>

      {/* Full Screen Demo Image */}
      {heroImage && (
        <section className="w-full">
          <div className="relative w-full aspect-[21/9] lg:aspect-auto lg:h-[400px]">
            <Image
              src={heroImage}
              alt={heroTitle || "Portfolio Banner"}
              fill
              className="object-cover"
              priority
            />
          </div>
        </section>
      )}

      {/* Project Details & Technologies (Dark Section) - Rendered if data is provided */}
      {(industry || solution || (tags && tags.length > 0)) && (
        <section className="bg-[#012A49] text-white py-6 tsm-400:py-8 sm:py-10 lg:py-12 xl:py-[75px] px-4 sm:px-6">
          <div className="md:max-w-[650px] lg:max-w-[820px] xl:max-w-[1150px] mx-auto">
            <div className="flex flex-col md:flex-row justify-center md:justify-between items-center gap-6 sm:gap-8">
              {/* Left side */}
              <div className="w-full md:w-[250px] lg:w-[300px] flex flex-col items-center md:items-start space-y-3 md:space-y-6">
                <h2 className="w-full text-[18px] lg:text-[24px] xl:text-[32px] tracking-wide leading-tight md:leading-none text-center md:text-left"
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}>
                  About The Project</h2>
                <div className="grid grid-cols-[max-content_1fr] lg:grid-cols-[100px_1fr] text-start justify-center md:justify-start gap-x-4 md:gap-4 text-[12px] lg:text-[14px] xl:text-[16px] text-white mt-4 md:mt-6"
                  style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.medium }}
                >
                  {industry && (
                    <>
                      <span>Industry :</span>
                      <span className="font-semibold">{industry}</span>
                    </>
                  )}
                  {solution && (
                    <>
                      <span>Solution :</span>
                      <span className="font-semibold">{solution}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-[1.5px] self-stretch bg-white/5"></div>

              {/* Right side */}
              <div className="tsm-350:w-[318px] tsm-400:w-[320px] sm:w-[380px] md:w-[450px] lg:w-[600px]">
                <h3 className="text-[14px] lg:text-[16px] xl:text-[18px] text-white font-medium mb-4 sm:mb-6 text-center md:text-left"
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold }}>
                  Technologies:</h3>
                <div className="flex flex-wrap gap-1 sm:gap-2" style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.medium }}>
                  {tags?.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-[#ffffff21] hover:bg-[#0058BF] transition-colors rounded-full text-[11px] sm:text-[12px] lg:text-[14px] xl:text-[16px] font-medium text-white shadow-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
